import {
  keepPreviousData,
  skipToken,
  useQuery,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import type { OdMapResult } from '../../types/domain';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import { useAggregationConfig } from '../SimViewer/useAggregationConfig';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { useDataFilter } from '../SimViewer/useDataFilter';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useOdMapConfig } from '../SimViewer/useOdMapConfig';

type UseOdMapData = {
  isLoading: boolean;
  data: OdMapResult;
};

const EMPTY_DATA = {
  nodes: [],
  edges: [],
  contours: [],
} as const satisfies OdMapResult;

const useOdMapData = (): UseOdMapData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { dataFilter } = useDataFilter();
  const { apiFilter } = useApiDataFilter();
  const { aggregationConfig } = useAggregationConfig();
  const { odMapType, showsAllOd } = useOdMapConfig();

  const isRequired =
    aggregationConfig?.type === 'od_map' &&
    (dataFilter.start.areaKeyCodes.length > 0 ||
      dataFilter.start.customAreaKeys.length > 0 ||
      dataFilter.end.areaKeyCodes.length > 0 ||
      dataFilter.end.customAreaKeys.length > 0 ||
      dataFilter.pass.wayIds.length > 0);
  const isLinkMode = dataFilter.pass.wayIds.length > 0;

  const { data, isFetching } = useQuery({
    queryKey: ['odMap', currentSimDataId, apiFilter, odMapType, showsAllOd],
    queryFn:
      currentSimDataId && isRequired
        ? async ({ signal }: QueryFunctionContext): Promise<OdMapResult> => {
            if (isLinkMode) {
              return dataApiClient.getLinkOdMap(
                currentSimDataId,
                odMapType,
                showsAllOd,
                apiFilter,
                signal
              );
            }
            return dataApiClient.getOdMap(
              currentSimDataId,
              odMapType,
              showsAllOd,
              apiFilter,
              signal
            );
          }
        : skipToken,
    placeholderData: keepPreviousData,
    meta: { dataType: 'aggregation' },
  });

  return {
    isLoading: isFetching,
    data: data && isRequired ? data : EMPTY_DATA,
  };
};

export { useOdMapData };
